const BASE_PATH = '/settings/partners';

export interface PartnerSettingsSubTab {
  label: string;
  to: string;
}

export interface PartnerSettingsTab {
  label: string;
  to: string;
  subTabs?: PartnerSettingsSubTab[];
}

/**
 * 取引先設定のタブ・サブタブ定義。
 * タブUI(PartnerTabs / PartnerSubTabs)と設定サイドバーの検索が同じ定義を参照する。
 */
export const partnerSettingsTabs: PartnerSettingsTab[] = [
  {
    label: '基本設定',
    to: `${BASE_PATH}/amount-calculation`,
    subTabs: [
      { label: '計算設定', to: `${BASE_PATH}/amount-calculation` },
      { label: '分類', to: `${BASE_PATH}/categories` },
      { label: 'タグ', to: `${BASE_PATH}/tags` },
      { label: '納品先ラベル', to: `${BASE_PATH}/delivery-destination-labels` },
      { label: 'ランク', to: `${BASE_PATH}/price-groups` },
    ],
  },
  {
    label: 'カスタム項目',
    to: `${BASE_PATH}/custom-properties`,
    subTabs: [
      { label: 'カスタム項目', to: `${BASE_PATH}/custom-properties` },
      { label: 'グループ', to: `${BASE_PATH}/custom-properties/groups` },
    ],
  },
  { label: '出力項目', to: `${BASE_PATH}/export-settings` },
  { label: '締めグループ', to: `${BASE_PATH}/closing-groups` },
  {
    label: '行動履歴',
    to: `${BASE_PATH}/activity-types`,
    subTabs: [
      { label: '種別', to: `${BASE_PATH}/activity-types` },
      { label: 'カスタム項目', to: `${BASE_PATH}/activity-custom-properties` },
    ],
  },
];

/**
 * パスがタブの遷移先配下かどうか。`/custom-properties` が `/activity-custom-properties` に
 * 誤マッチしないよう、パスセグメント境界で前方一致させる。
 */
const isPathMatched = (pathname: string, to: string): boolean =>
  pathname === to || pathname.startsWith(`${to}/`);

/** マッチしたパスの長さ。マッチしない場合は0を返す。より長い(=具体的な)定義を優先するために使う。 */
const matchedLength = (pathname: string, to: string): number => (isPathMatched(pathname, to) ? to.length : 0);

const tabMatchedLength = (pathname: string, tab: PartnerSettingsTab): number =>
  Math.max(
    matchedLength(pathname, tab.to),
    ...(tab.subTabs ?? []).map((subTab) => matchedLength(pathname, subTab.to)),
  );

/** パスに対応するタブ。どのタブにも属さないパスの場合は undefined を返す。 */
export const findPartnerSettingsTab = (pathname: string): PartnerSettingsTab | undefined => {
  let matched: PartnerSettingsTab | undefined;
  let matchedTo = 0;

  for (const tab of partnerSettingsTabs) {
    const length = tabMatchedLength(pathname, tab);
    if (length > matchedTo) {
      matched = tab;
      matchedTo = length;
    }
  }

  return matched;
};

/** パスに対応するタブのインデックス。どのタブにも属さない場合は先頭タブを選択状態にする。 */
export const findPartnerSettingsTabIndex = (pathname: string): number => {
  const matched = findPartnerSettingsTab(pathname);
  const index = matched ? partnerSettingsTabs.indexOf(matched) : -1;
  return index === -1 ? 0 : index;
};

/** パスに対応するサブタブのインデックス。どのサブタブにも属さない場合は先頭サブタブを選択状態にする。 */
export const findPartnerSettingsSubTabIndex = (tab: PartnerSettingsTab, pathname: string): number => {
  let index = 0;
  let matchedTo = 0;

  (tab.subTabs ?? []).forEach((subTab, i) => {
    const length = matchedLength(pathname, subTab.to);
    if (length > matchedTo) {
      index = i;
      matchedTo = length;
    }
  });

  return index;
};
